import { jsPDF } from 'jspdf';

const A4        = { w: 210, h: 297 };
const MARGIN    = 18;
const CONTENT_W = A4.w - MARGIN * 2;

// ─── Markdown → HTML (for chat messages) ─────────────────────────────────────
// Oberon responses are markdown. Convert to HTML so the existing HTML→blocks
// pipeline can handle bold, lists, headings, etc.

function markdownToHtml(text) {
  if (!text) return '';

  function inlineMd(line) {
    return line
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g,     '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g,     '<em>$1</em>')
      .replace(/_(.*?)_/g,       '<em>$1</em>');
  }

  // OpenAI sometimes returns numbered/bullet lists without newlines between items,
  // e.g. "Intro: 1. First item 2. Second item 3. Third item"
  // Normalise these onto separate lines before line-by-line parsing.
  const normalized = text
    .replace(/([^\n])[ \t]+(\d+[.)]\s)/g, '$1\n$2')  // "text 2. " → "text\n2. "
    .replace(/([^\n])[ \t]+(- )/g,        '$1\n$2');   // "text - " → "text\n- "

  const out    = [];
  const lines  = normalized.split('\n');
  let listType = null; // 'ol' | 'ul' | null

  function closeList() {
    if (listType) { out.push(`</${listType}>`); listType = null; }
  }

  for (let i = 0; i < lines.length; i++) {
    const raw     = lines[i];
    const trimmed = raw.trim();

    // Heading: # … ###### text
    const hm = trimmed.match(/^(#{1,6})\s+(.+)/);
    if (hm) {
      closeList();
      out.push(`<h${hm[1].length}>${inlineMd(hm[2])}</h${hm[1].length}>`);
      continue;
    }

    // Ordered list item: 1. or 1)
    const olm = trimmed.match(/^(\d+)[.)]\s+([\s\S]+)/);
    if (olm) {
      if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol'; }
      out.push(`<li>${inlineMd(olm[2])}</li>`);
      continue;
    }

    // Unordered list item: - or *
    const ulm = trimmed.match(/^[-*]\s+([\s\S]+)/);
    if (ulm) {
      if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul'; }
      out.push(`<li>${inlineMd(ulm[1])}</li>`);
      continue;
    }

    // Blank line — if inside a list, skip (GPT often puts blank lines between items);
    // otherwise emit a paragraph break.
    if (!trimmed) {
      if (!listType) out.push('<br>');
      continue;
    }

    // Regular text — close list and emit as paragraph
    closeList();
    out.push(`<p>${inlineMd(trimmed)}</p>`);
  }

  closeList();
  return out.join('');
}

// ─── HTML → block structure (DOM-based) ─────────────────────────────────────
// Use the browser's own parser so real HTML tables and tags are handled exactly.

/**
 * Recursively collect inline text segments from a DOM node.
 * Returns [{text, bold}] — bold is set when inside <strong> or <b>.
 */
function getInlineSegments(node, parentBold = false) {
  const segments = [];

  function walk(n, bold) {
    if (n.nodeType === Node.TEXT_NODE) {
      const text = n.textContent.replace(/\s+/g, ' ');
      if (text) segments.push({ text, bold });
    } else if (n.nodeType === Node.ELEMENT_NODE) {
      const tag = n.tagName.toLowerCase();
      const isBold = bold || tag === 'strong' || tag === 'b';
      for (const child of n.childNodes) walk(child, isBold);
    }
  }

  walk(node, parentBold);

  // Merge adjacent same-style runs
  const merged = [];
  for (const seg of segments) {
    if (merged.length && merged[merged.length - 1].bold === seg.bold) {
      merged[merged.length - 1].text += seg.text;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged.filter(s => s.text.trim());
}

const BLOCK_TAGS = new Set([
  'p','div','h1','h2','h3','h4','h5','h6',
  'ul','ol','table','blockquote','pre','section','article'
]);

/** Convert an HTML string to a list of typed blocks. */
function parseHtmlContent(html) {
  const parser   = new DOMParser();
  const parsed   = parser.parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root     = parsed.querySelector('div');
  const blocks   = [];
  processChildren(root, blocks);
  return blocks;
}

/**
 * Walk the direct children of a container, collecting inline content
 * (text nodes, <strong>, <em>, <br>) into paragraph blocks and dispatching
 * block-level elements (<p>, <table>, <ul>, etc.) to processBlock().
 */
function processChildren(container, blocks) {
  let pendingSegs = [];
  let brCount     = 0;

  function flushPending() {
    const segs = mergePending(pendingSegs);
    if (segs.length) blocks.push({ type: 'paragraph', segments: segs });
    pendingSegs = [];
  }

  function mergePending(segs) {
    const out = [];
    for (const s of segs) {
      if (!s.text.trim()) continue;
      if (out.length && out[out.length - 1].bold === s.bold) {
        out[out.length - 1].text += s.text;
      } else {
        out.push({ ...s });
      }
    }
    return out;
  }

  for (const child of container.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent.replace(/\s+/g, ' ');
      if (text.trim()) { pendingSegs.push({ text, bold: false }); brCount = 0; }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();

      if (tag === 'br') {
        brCount++;
        if (brCount >= 2) { flushPending(); brCount = 0; }
      } else if (tag === 'strong' || tag === 'b') {
        pendingSegs.push({ text: child.textContent, bold: true });
        brCount = 0;
      } else if (tag === 'em' || tag === 'i') {
        pendingSegs.push({ text: child.textContent, bold: false });
        brCount = 0;
      } else if (BLOCK_TAGS.has(tag)) {
        flushPending();
        brCount = 0;
        processBlock(child, blocks);
      } else {
        const text = child.textContent.replace(/\s+/g, ' ');
        if (text.trim()) { pendingSegs.push({ text, bold: false }); brCount = 0; }
      }
    }
  }
  flushPending();
}

function processBlock(node, blocks) {
  const tag = node.tagName.toLowerCase();

  if (/^h[1-6]$/.test(tag)) {
    const segs = getInlineSegments(node);
    if (segs.length) blocks.push({ type: 'heading', level: parseInt(tag[1]), segments: segs });

  } else if (tag === 'p') {
    const segs = getInlineSegments(node);
    if (segs.length) blocks.push({ type: 'paragraph', segments: segs });

  } else if (tag === 'ul') {
    const items = [...node.querySelectorAll(':scope > li')].map(li => getInlineSegments(li));
    if (items.length) blocks.push({ type: 'list', items, ordered: false });

  } else if (tag === 'ol') {
    const items = [...node.querySelectorAll(':scope > li')].map(li => getInlineSegments(li));
    if (items.length) blocks.push({ type: 'list', items, ordered: true });

  } else if (tag === 'table') {
    const thEls    = [...node.querySelectorAll('thead tr th')];
    const header   = thEls.map(th => getCellContent(th, true)); // force bold for header cells
    const bodyRows = [...node.querySelectorAll('tbody tr')].map(tr =>
      [...tr.querySelectorAll('td, th')].map(cell => getCellContent(cell, false))
    );

    let allRows;
    if (header.length || bodyRows.length) {
      allRows = header.length ? [header, ...bodyRows] : bodyRows;
    } else {
      allRows = [...node.querySelectorAll('tr')].map(tr =>
        [...tr.querySelectorAll('td, th')].map(cell => getCellContent(cell, false))
      );
    }

    if (allRows.length) blocks.push({ type: 'table', rows: allRows, hasHeader: header.length > 0 });

  } else {
    processChildren(node, blocks);
  }
}

/**
 * Parse a table cell (<td> or <th>) into an array of cell content items.
 * Each item is either:
 *   { type: 'inline', segments: [{text, bold}] }
 *   { type: 'list',   items: [[{text, bold}]], ordered: boolean }
 *
 * This handles cells that contain plain text, inline bold/normal runs,
 * AND cells that contain <ul>/<ol> lists as children.
 */
function getCellContent(cell, forceBold = false) {
  const content     = [];
  let   pendingSegs = [];

  function flushPending() {
    const merged = [];
    for (const s of pendingSegs) {
      if (!s.text.trim()) continue;
      if (merged.length && merged[merged.length - 1].bold === s.bold) {
        merged[merged.length - 1].text += s.text;
      } else {
        merged.push({ ...s });
      }
    }
    if (merged.length) content.push({ type: 'inline', segments: merged });
    pendingSegs = [];
  }

  function walkCell(n, bold) {
    if (n.nodeType === Node.TEXT_NODE) {
      const text = n.textContent.replace(/\s+/g, ' ');
      if (text.trim()) pendingSegs.push({ text, bold });
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;

    const tag = n.tagName.toLowerCase();

    if (tag === 'ul' || tag === 'ol') {
      flushPending();
      const items = [...n.querySelectorAll(':scope > li')].map(li => getInlineSegments(li, bold));
      if (items.length) content.push({ type: 'list', items, ordered: tag === 'ol' });
    } else if (tag === 'p' || tag === 'div') {
      flushPending();
      const segs = getInlineSegments(n, bold);
      if (segs.length) content.push({ type: 'inline', segments: segs });
    } else if (tag === 'br') {
      // treat <br> inside a cell as a soft break — flush current paragraph
      flushPending();
    } else {
      const isBold = bold || tag === 'strong' || tag === 'b';
      for (const child of n.childNodes) walkCell(child, isBold);
    }
  }

  for (const child of cell.childNodes) walkCell(child, forceBold);
  flushPending();

  if (forceBold) {
    // Ensure all segments are bold (header cells)
    for (const item of content) {
      if (item.type === 'inline') {
        item.segments = item.segments.map(s => ({ ...s, bold: true }));
      } else if (item.type === 'list') {
        item.items = item.items.map(row => row.map(s => ({ ...s, bold: true })));
      }
    }
  }

  return content;
}

// ─── Mixed bold/normal text rendering for jsPDF ──────────────────────────────
// jsPDF can't render mixed styles in one call, so we split text into word
// tokens, flow them into lines respecting maxWidth, then draw word-by-word.

function buildTokens(doc, segments, size) {
  doc.setFontSize(size);
  const tokens = [];
  for (const seg of (segments || [])) {
    doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
    const spW   = doc.getTextWidth(' ');
    const words = seg.text.split(/\s+/).filter(Boolean);
    for (const word of words) {
      tokens.push({ text: word, bold: seg.bold, w: doc.getTextWidth(word), spW });
    }
  }
  return tokens;
}

function flowIntoLines(tokens, maxWidth) {
  const lines = [];
  let cur = [], curW = 0;

  for (const tok of tokens) {
    const spW    = cur.length ? tok.spW : 0;
    const needed = spW + tok.w;

    if (curW + needed > maxWidth && cur.length) {
      lines.push(cur);
      cur  = [tok];
      curW = tok.w;
    } else {
      if (cur.length) cur.push({ isSpace: true, w: spW });
      cur.push(tok);
      curW += needed;
    }
  }
  if (cur.length) lines.push(cur);
  return lines;
}

function drawTokenLine(doc, lineTokens, x, y, size) {
  doc.setFontSize(size);
  let cx = x;
  for (const tok of lineTokens) {
    if (tok.isSpace) { cx += tok.w; continue; }
    doc.setFont('helvetica', tok.bold ? 'bold' : 'normal');
    doc.text(tok.text, cx, y);
    cx += tok.w;
  }
}

// ─── Block-level renderers ────────────────────────────────────────────────────

function checkPage(doc, y, needed = 12) {
  if (y + needed > A4.h - MARGIN) { doc.addPage(); return MARGIN + 6; }
  return y;
}

function drawHRule(doc, y) {
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, A4.w - MARGIN, y);
  return y + 5;
}

/** Render segments with mixed bold/normal at (MARGIN, y), returns new y. */
function renderSegments(doc, segments, y, { size = 10, lineH = 5.2, x = MARGIN, width = CONTENT_W } = {}) {
  if (!segments?.length) return y;
  const lines = flowIntoLines(buildTokens(doc, segments, size), width);
  for (const line of lines) {
    y = checkPage(doc, y, lineH + 2);
    drawTokenLine(doc, line, x, y, size);
    y += lineH;
  }
  return y;
}

/** Render a plain string (no mixed styles). */
function renderText(doc, text, y, { size = 10, style = 'normal', lineH = 5.2, x = MARGIN, width = CONTENT_W } = {}) {
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  for (const line of doc.splitTextToSize(String(text ?? ''), width)) {
    y = checkPage(doc, y, lineH + 2);
    doc.text(line, x, y);
    y += lineH;
  }
  return y;
}

function renderList(doc, items, y, ordered = false) {
  const BULLET_X   = MARGIN + 4;
  const CONTENT_X  = MARGIN + 9;
  const textW      = CONTENT_W - 9;

  for (let i = 0; i < items.length; i++) {
    const prefix = ordered ? `${i + 1}.` : '•';
    const lines  = flowIntoLines(buildTokens(doc, items[i], 10), textW);

    for (let li = 0; li < lines.length; li++) {
      y = checkPage(doc, y, 5);
      if (li === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(prefix, BULLET_X, y);
      }
      drawTokenLine(doc, lines[li], CONTENT_X, y, 10);
      y += 5;
    }
    y += 0.5;
  }
  return y;
}

/** Count how many rendered lines a cell's content occupies at a given column width. */
function countCellLines(doc, cellContent, textW, size, lineH) {
  const BULLET_W = 3.5;
  let total = 0;
  for (const item of cellContent) {
    if (item.type === 'inline') {
      total += flowIntoLines(buildTokens(doc, item.segments, size), textW).length;
    } else if (item.type === 'list') {
      for (const listItem of item.items) {
        total += flowIntoLines(buildTokens(doc, listItem, size), textW - BULLET_W).length;
      }
    }
  }
  return Math.max(1, total);
}

/** Render a cell's content items (inline segments and/or nested lists) inside a cell. */
function renderCellContent(doc, cellContent, cellX, rowTopY, cellW, size, lineH, padH) {
  const BULLET_W = 3.5;
  const textX    = cellX + padH;
  const textW    = cellW - padH * 2;
  let   baseline = rowTopY + padH + lineH; // first text baseline

  for (const item of cellContent) {
    if (item.type === 'inline') {
      const lines = flowIntoLines(buildTokens(doc, item.segments, size), textW);
      for (const lineTokens of lines) {
        drawTokenLine(doc, lineTokens, textX, baseline, size);
        baseline += lineH;
      }
    } else if (item.type === 'list') {
      for (const listItem of item.items) {
        const itemLines = flowIntoLines(buildTokens(doc, listItem, size), textW - BULLET_W);
        for (let i = 0; i < itemLines.length; i++) {
          if (i === 0) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(size);
            doc.text('•', textX, baseline);
          }
          drawTokenLine(doc, itemLines[i], textX + BULLET_W, baseline, size);
          baseline += lineH;
        }
      }
    }
  }
}

function renderTable(doc, rows, startY, hasHeader = true) {
  if (!rows?.length) return startY;
  const numCols = Math.max(...rows.map(r => r.length));
  if (!numCols) return startY;

  const SIZE   = 9;
  const LINE_H = 4.5;
  const PAD_V  = 2.5;
  const PAD_H  = 2;
  const colW   = CONTENT_W / numCols;
  const textW  = colW - PAD_H * 2;

  let y = startY;

  for (let rIdx = 0; rIdx < rows.length; rIdx++) {
    const isHeader = hasHeader && rIdx === 0;
    const row      = rows[rIdx];

    // Pre-calculate row height from the tallest cell
    const maxLines = Math.max(1, ...row.map(cell => countCellLines(doc, cell, textW, SIZE, LINE_H)));
    const rowH     = PAD_V + maxLines * LINE_H + PAD_V;

    y = checkPage(doc, y, rowH + 2);

    // Header background
    if (isHeader) {
      doc.setFillColor(235, 235, 235);
      doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
    }

    // Row border
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.15);
    doc.rect(MARGIN, y, CONTENT_W, rowH);

    // Render each cell
    row.forEach((cellContent, cIdx) => {
      const cellX = MARGIN + cIdx * colW;
      if (cIdx > 0) {
        doc.setDrawColor(190, 190, 190);
        doc.line(cellX, y, cellX, y + rowH);
      }
      renderCellContent(doc, cellContent, cellX, y, colW, SIZE, LINE_H, PAD_H);
    });

    y += rowH;
  }

  return y + 2;
}

// ─── Block renderer ───────────────────────────────────────────────────────────

const H_SIZES = { 1: 15, 2: 13, 3: 12, 4: 11, 5: 10, 6: 10 };

function renderBlocks(doc, blocks, startY) {
  let y = startY;

  for (const block of blocks) {
    switch (block.type) {
      case 'heading': {
        const sz = H_SIZES[block.level] || 11;
        y += block.level <= 2 ? 4 : 2;
        y  = checkPage(doc, y, sz + 4);
        y  = renderSegments(doc, block.segments, y, { size: sz, lineH: sz * 0.45 + 2 });
        y += block.level <= 2 ? 3 : 1;
        break;
      }
      case 'paragraph': {
        // A paragraph whose every segment is bold and is short is acting as
        // a section header (common in AI-generated feedback that uses <strong>
        // instead of heading tags).
        const allBold = block.segments.length > 0 && block.segments.every(s => s.bold);
        const charLen = block.segments.reduce((n, s) => n + s.text.length, 0);
        if (allBold && charLen < 80) {
          y += 3;
          y  = checkPage(doc, y, 10);
          y  = renderSegments(doc, block.segments, y, { size: 12, lineH: 7 });
          y += 2;
        } else {
          y = renderSegments(doc, block.segments, y, { size: 10, lineH: 5.2 });
          y += 2;
        }
        break;
      }
      case 'list':
        y = renderList(doc, block.items, y, block.ordered);
        y += 2;
        break;
      case 'table':
        y = checkPage(doc, y, 20);
        y = renderTable(doc, block.rows, y, block.hasHeader);
        y += 3;
        break;
      case 'rule':
        y = drawHRule(doc, y);
        break;
      case 'spacer':
        y += 2;
        break;
    }
  }
  return y;
}

// ─── Public API ───────────────────────────────────────────────────────────────

function dateStr() {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function safeSlug(str, max = 40) {
  return (str || 'file').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, max);
}

/**
 * Download the current Oberon chat session as a PDF.
 */
export function exportChatAsPdf(messages, contextTitle = 'Chat Session') {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN + 4;

  y = renderText(doc, 'Oberon Chat Session', y, { size: 16, style: 'bold', lineH: 9 });
  if (contextTitle) y = renderText(doc, contextTitle, y, { size: 11, lineH: 6 });
  y = renderText(doc, `Downloaded: ${dateStr()}`, y, { size: 9, lineH: 5 });
  y += 4;
  y = drawHRule(doc, y);

  for (const msg of messages.filter(m => m.content && !m.isError)) {
    y = renderText(doc, msg.role === 'user' ? 'You' : 'Oberon', y, { size: 10, style: 'bold', lineH: 5.5 });
    const html = markdownToHtml(msg.content);
    y = renderBlocks(doc, parseHtmlContent(html), y);
    y += 4;
  }

  doc.save(`oberon-${safeSlug(contextTitle)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Download chapter feedback as a PDF.
 * Headings, bullets, bold text, and tables are all rendered with proper formatting.
 */
export function exportFeedbackAsPdf(chapterTitle, questionsAndAnswers, feedbackText) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN + 4;

  y = renderText(doc, 'Chapter Feedback', y, { size: 16, style: 'bold', lineH: 9 });
  y = renderText(doc, chapterTitle || '', y, { size: 13, lineH: 7.5 });
  y = renderText(doc, `Downloaded: ${dateStr()}`, y, { size: 9, lineH: 5 });
  y += 4;
  y = drawHRule(doc, y);

  if (questionsAndAnswers?.length > 0) {
    y = renderText(doc, 'Your Responses', y, { size: 12, style: 'bold', lineH: 7 });
    y += 2;

    questionsAndAnswers.forEach((qa, idx) => {
      y = renderText(doc, `Q${idx + 1}:`, y, { size: 10, style: 'bold', lineH: 5.5 });
      y = renderBlocks(doc, parseHtmlContent(qa.question || ''), y);
      y += 1;

      let answerText;
      if (qa.tableData) {
        const header   = (qa.tableData.headings || []).join('  |  ');
        const dataRows = (qa.tableData.rows || []).map(r => (r || []).join('  |  ')).join('\n');
        answerText     = [header, dataRows].filter(Boolean).join('\n');
      } else if (Array.isArray(qa.answer)) {
        answerText = qa.answer.join(', ');
      } else {
        answerText = String(qa.answer ?? '');
      }
      y = renderText(doc, `Answer: ${answerText}`, y, { size: 10, lineH: 5 });
      y += 5;
    });

    y += 2;
    y = drawHRule(doc, y);
  }

  y = renderText(doc, 'Feedback', y, { size: 12, style: 'bold', lineH: 7 });
  y += 2;
  y = renderBlocks(doc, parseHtmlContent(feedbackText || ''), y);

  doc.save(`feedback-${safeSlug(chapterTitle)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
